// client/src/stores/ui-store.ts — Zustand, UI state ONLY (04 §9.5; doc 05 UI-001 AC3).
//
// THE LITMUS TEST, applied to every field below:
//   • if the server could ever disagree with it  -> server state, belongs in TanStack Query;
//   • if the URL should restore it               -> belongs in Zod-validated search params (§9.2.3);
//   • only what is left may enter this store.
//
// Consequences, enforced by review: no list filters, pagination or sort here (those are URL
// state); no rows, entities or DTOs here; this file never imports from `src/client`.
//
// ONE slice persists, and only one: `onboarding` (UI-006, D-740). Everything else is still
// memory-only — see `partialize` at the bottom and client/README.md for why the exception is
// the exception.

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * What a single organization has already done in the first-run wizard (SCR-ONB-001).
 *
 * The three ids are the ONLY server-derived values this store is allowed to hold, and they are
 * not server state by the litmus test: nothing on the server can disagree about „which property
 * did THIS BROWSER create in step 1", which is the only question they answer. They are here so
 * that a refresh in the middle of the wizard resumes at the next step instead of creating a
 * second property — the whole of 05 UI-006's „resumable".
 *
 * Every member is optional and absent means „not yet": a progress object read back from a
 * previous version of this store, or from a browser that stopped halfway, is still a valid one.
 */
export interface OnboardingProgress {
  /** The property step 1 created, once it exists. */
  propertyId?: string;
  /** The tenant step 2 created. */
  tenantId?: string;
  /** The contract step 3 created AND activated (D-742). */
  contractId?: string;
  /** „Sari peste" was taken, or the wizard was finished — either way, do not offer it again. */
  dismissed?: boolean;
  /** The `OnboardingChecklist` ids the landlord has ticked away, one by one. */
  checklistDismissed?: readonly string[];
}

const NO_PROGRESS: OnboardingProgress = {};

interface UiState {
  /** Mobile navigation drawer — pure viewport state, meaningless to the server. */
  mobileDrawerOpen: boolean;
  /** Settings sub-navigation collapse (§8.18). */
  settingsNavCollapsed: boolean;
  /** Table density preference; not persisted in V1. */
  tableDensity: 'comfortable' | 'compact';

  /**
   * The §6.4 support modal. Open/closed is viewport state by the litmus test above, and it
   * lives here rather than inside the button because §6.4 mounts the SAME control twice —
   * the footer's „Grup de suport" and the header's „?" — and a page with two dialogs in it
   * has two focus traps to get wrong.
   */
  supportOpen: boolean;
  /**
   * Correlation id of the failure the modal was opened from, when it was opened from one
   * (§6.4 prefills it into the mailto). Not server state: it is a string the user is ALREADY
   * looking at on the error surface, carried across so they do not have to transcribe it.
   */
  supportCorrelationId: string | undefined;

  /**
   * First-run progress, KEYED BY ORGANIZATION. A landlord who owns two organizations onboards
   * each of them, and a wizard that remembered „done" globally would leave the second one with
   * an empty dashboard and no way back to the flow that fills it.
   */
  onboarding: Record<string, OnboardingProgress>;

  setMobileDrawerOpen: (open: boolean) => void;
  toggleMobileDrawer: () => void;
  setSettingsNavCollapsed: (collapsed: boolean) => void;
  setTableDensity: (density: UiState['tableDensity']) => void;
  /**
   * The desktop rail folded to its icons (D-1838). Viewport state by the litmus test; memory-only
   * like every slice but `onboarding`, because 04 §9.5 allows no other persistence.
   */
  railCollapsed: boolean;
  toggleRailCollapsed: () => void;
  /**
   * §6.2 groups the owner has folded shut, by group id (D-1850). Memory-only like the rail's
   * fold. A press folds any group, the one holding the current page included (D-2940).
   */
  collapsedNavGroups: Record<string, boolean>;
  toggleNavGroup: (groupId: string) => void;
  /** Unfold one group; a no-op when it is already open, so it never re-renders the rail. */
  openNavGroup: (groupId: string) => void;
  /** The Ctrl+K module search (D-1841) — open/closed, nothing else. */
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  openSupport: (correlationId?: string) => void;
  closeSupport: () => void;
  /** Merge into one organization's progress — the ids as each step produces them. */
  recordOnboarding: (organizationId: string, progress: OnboardingProgress) => void;
  /** Tick one checklist row away. Idempotent: a second call adds no second id. */
  dismissChecklistStep: (organizationId: string, stepId: string) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      mobileDrawerOpen: false,
      settingsNavCollapsed: false,
      tableDensity: 'comfortable',
      railCollapsed: false,
      collapsedNavGroups: {},
      commandPaletteOpen: false,
      supportOpen: false,
      supportCorrelationId: undefined,
      onboarding: {},

      setMobileDrawerOpen: (open) => set({ mobileDrawerOpen: open }),
      toggleMobileDrawer: () => set((state) => ({ mobileDrawerOpen: !state.mobileDrawerOpen })),
      setSettingsNavCollapsed: (collapsed) => set({ settingsNavCollapsed: collapsed }),
      setTableDensity: (density) => set({ tableDensity: density }),
      toggleRailCollapsed: () => set((state) => ({ railCollapsed: !state.railCollapsed })),
      toggleNavGroup: (groupId) =>
        set((state) => ({
          collapsedNavGroups: {
            ...state.collapsedNavGroups,
            [groupId]: !state.collapsedNavGroups[groupId],
          },
        })),
      openNavGroup: (groupId) =>
        set((state) =>
          state.collapsedNavGroups[groupId]
            ? { collapsedNavGroups: { ...state.collapsedNavGroups, [groupId]: false } }
            : state,
        ),
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
      openSupport: (correlationId) =>
        set({ supportOpen: true, supportCorrelationId: correlationId }),
      // The id is cleared with the modal: the next „Grup de suport" click is a question, not a
      // report about whatever failed twenty minutes ago.
      closeSupport: () => set({ supportOpen: false, supportCorrelationId: undefined }),

      recordOnboarding: (organizationId, progress) =>
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            [organizationId]: { ...(state.onboarding[organizationId] ?? NO_PROGRESS), ...progress },
          },
        })),

      dismissChecklistStep: (organizationId, stepId) =>
        set((state) => {
          const current = state.onboarding[organizationId] ?? NO_PROGRESS;
          const dismissed = current.checklistDismissed ?? [];
          if (dismissed.includes(stepId)) return state;
          return {
            onboarding: {
              ...state.onboarding,
              [organizationId]: { ...current, checklistDismissed: [...dismissed, stepId] },
            },
          };
        }),
    }),
    {
      name: 'prop360.onboarding',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      // THE WHOLE OF WHAT IS WRITTEN TO DISK. 04 §9.5's rule — „stores never persist to
      // localStorage in V1" — was written about the ACCESS TOKEN, which stays memory-only in
      // `auth-store.ts` and is not reachable from here. The same section's own row for this
      // store nevertheless lists „dismissed onboarding checklist steps", and a dismissal that
      // dies with the tab is not a dismissal. So the exception is drawn as narrowly as it can
      // be: this key and no other, three opaque ids and two flags, nothing a script that read
      // them could authenticate with. See DEVIATIONS D-740.
      partialize: (state) => ({ onboarding: state.onboarding }),
    },
  ),
);

/** One organization's progress, never undefined — „nothing yet" and „no such key" are one state. */
export function onboardingProgressOf(
  state: Pick<UiState, 'onboarding'>,
  organizationId: string,
): OnboardingProgress {
  return state.onboarding[organizationId] ?? NO_PROGRESS;
}
